import CancelIcon from '@material-ui/icons/Cancel';
import React from 'react';
import {SPLIT_DELIMITER, splitAndRemoveEmpty} from '../../../placement/const';
import {BusinessContactFilterValue, hasSearch} from './domain/filter';
import {BusinessFunctionOption} from './useBusinessFunctionOptions';

type Chip = {
    key: string;
    text: string;
    /** What the remove button announces, when the text alone would not say which chip it is. */
    label?: string;
    remove: () => void;
}

type Props = {
    filter: BusinessContactFilterValue;
    /** The picker's options, so a chip reads the label the user picked rather than the stored value. */
    options: BusinessFunctionOption[];
    onChange: (filter: BusinessContactFilterValue) => void;
};

/** One removable chip. The title keeps the whole text when a long one is cut short. */
const ChipButton = ({chip}: {chip: Chip}) => (
    <div className='mr-2 mb-2 bg-botticelli-100 text-white p-2 rounded leading-none flex items-center max-w-xs'
         title={chip.text}>
        <span className='truncate'>{chip.text}</span>
        <button type='button'
                aria-label={`Remove ${chip.label ?? chip.text}`}
                className='bg-transparent outline-none focus:outline-none flex-shrink-0'
                onClick={chip.remove}>
            <div className='bg-transparent text-white opacity-5 ml-3 flex items-center'>
                <CancelIcon fontSize={'small'}/>
            </div>
        </button>
    </div>
);

/** A label and its chips. The chips wrap under their own label, so the two groups never mix. */
const ChipGroup = ({label, chips}: {label: string; chips: Chip[]}) => (
    <div className='flex items-start mr-10'>
        <div className='pt-2 pr-5 flex-grow-0 whitespace-no-wrap'>
            {label}
        </div>
        <div className='flex flex-grow flex-wrap'>
            {chips.map((chip) => <ChipButton key={chip.key} chip={chip}/>)}
        </div>
    </div>
);

/**
 * The applied funnel values as removable chips: the typed search under "Search:", the picks under
 * "Filter:" in the shape of the Client Care list's. A label shows only when it has a chip.
 */
export const BusinessContactFilterBadge = ({filter, options, onChange}: Props) => {
    // A value Salesforce no longer offers has no label; it shows as stored so it can still be removed.
    const labelOf = (value: string) => options.find((option) => option.value === value)?.label ?? value;
    const picks = splitAndRemoveEmpty(filter.businessFunction);

    const searchChips: Chip[] = hasSearch(filter)
        ? [{
            key: 'search',
            text: filter.search,
            label: `search ${filter.search}`,
            remove: () => onChange({...filter, search: ''}),
        }]
        : [];

    const pickChips: Chip[] = picks.map((value) => ({
        key: `function-${value}`,
        text: labelOf(value),
        remove: () => onChange({
            ...filter,
            businessFunction: picks.filter((pick) => pick !== value).join(SPLIT_DELIMITER),
        }),
    }));
    if (filter.status !== '') {
        pickChips.push({
            key: 'status',
            text: `Status: ${filter.status}`,
            remove: () => onChange({...filter, status: ''}),
        });
    }

    if (searchChips.length === 0 && pickChips.length === 0) {
        return null;
    }

    return (
        <div className='flex flex-wrap items-start mb-2'>
            {searchChips.length > 0 && <ChipGroup label='Search:' chips={searchChips}/>}
            {pickChips.length > 0 && <ChipGroup label='Filter:' chips={pickChips}/>}
        </div>
    );
};
